/**
 * Theme resolution registers — normative sources:
 * 07-02-screen-inventory.md §Floor-Mode Screens and
 * 07-11-design-system.md §Token Values — `dense`.
 *
 * The registers live here, beside the screen inventory they enumerate, because
 * `@beatq/ui` already depends on `@beatq/shared` and `ScreenFrame` must resolve
 * a theme without inverting that edge. `@beatq/ui` re-exports them, so the
 * design system remains the public authority for theme resolution.
 *
 * These are explicit enumerations rather than screen-id prefix matches: the
 * registers are not contiguous ranges, and a prefix test silently pulls in
 * neighbouring screens.
 */

export type ThemeMode = 'standard' | 'floor' | 'dense';

/** `SCR-MER-063`–`072`, `SCR-MER-088`, `SCR-MER-089`, `SCR-CHS-001`–`006` — eighteen screens. */
export const FLOOR_SCREEN_IDS: readonly string[] = [
  ...Array.from({ length: 10 }, (_, i) => `SCR-MER-${String(63 + i).padStart(3, '0')}`),
  'SCR-MER-088',
  'SCR-MER-089',
  ...Array.from({ length: 6 }, (_, i) => `SCR-CHS-${String(1 + i).padStart(3, '0')}`),
];

/**
 * Admin Console tabular and investigation surfaces only. `dense` is never
 * applied to a surface carrying a consequential action, which resolves
 * `standard` even inside a dense page.
 */
export const DENSE_SCREEN_IDS: readonly string[] = [
  'SCR-ADM-003',
  'SCR-ADM-007',
  'SCR-ADM-008',
  'SCR-ADM-009',
  'SCR-ADM-010',
  'SCR-ADM-011',
  'SCR-ADM-020',
  'SCR-ADM-024',
  'SCR-ADM-028',
  'SCR-ADM-034',
  'SCR-ADM-037',
  'SCR-ADM-044',
  'SCR-ADM-045',
];

const FLOOR = new Set(FLOOR_SCREEN_IDS);
const DENSE = new Set(DENSE_SCREEN_IDS);

export function isFloorScreen(screenId: string): boolean {
  return FLOOR.has(screenId);
}

export function isDenseScreen(screenId: string): boolean {
  return DENSE.has(screenId);
}

/**
 * 07-11: "Theme selection is declared per route in the application, resolved by
 * the design system. A user cannot select a theme."
 */
export function resolveThemeForScreen(screenId: string): ThemeMode {
  if (isFloorScreen(screenId)) return 'floor';
  if (isDenseScreen(screenId)) return 'dense';
  return 'standard';
}
